import React from 'react';
import AnimatedCounter from '../../components/ui/AnimatedCounter';
import FeatureShowcase from '../../components/ui/FeatureShowcase';
import { Car, Truck, Bus, Bike } from 'lucide-react';

const ContainerTruck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-white">
    <rect x="1" y="5" width="15" height="10" rx="1"/><path d="M16 8h4l2 4v3h-6V8z"/><circle cx="5.5" cy="18.5" r="1.5"/><circle cx="18.5" cy="18.5" r="1.5"/>
  </svg>
);

const AutoRickshaw = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-white">
    <path d="M4 15V9a2 2 0 012-2h8l3 5v3H4z"/><circle cx="7" cy="18" r="1.5"/><circle cx="16" cy="18" r="1.5"/>
  </svg>
);

const HeavyVehicle = () => (
  <svg viewBox="0 0 32 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-4 text-white">
    {/* Cab */}
    <rect x="1" y="3" width="6" height="7" rx="0.5"/>
    <rect x="2" y="4" width="4" height="3" rx="0.3"/>
    {/* First trailer */}
    <rect x="7" y="4" width="10" height="6" rx="0.5"/>
    {/* Second trailer */}
    <rect x="17.5" y="4" width="12" height="6" rx="0.5"/>
    {/* Cab wheels */}
    <circle cx="3" cy="11.5" r="1.2"/>
    <circle cx="6" cy="11.5" r="1.2"/>
    {/* First trailer wheels */}
    <circle cx="10" cy="11.5" r="1.2"/>
    <circle cx="13" cy="11.5" r="1.2"/>
    {/* Second trailer front wheels */}
    <circle cx="19.5" cy="11.5" r="1.2"/>
    <circle cx="22.5" cy="11.5" r="1.2"/>
    {/* Second trailer rear wheels */}
    <circle cx="26" cy="11.5" r="1.2"/>
    <circle cx="29" cy="11.5" r="1.2"/>
  </svg>
);

const TrackingVisual: React.FC = () => (
  <div className="aspect-[4/3] bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 lg:p-8 flex items-center justify-center relative overflow-hidden">
    {/* Map Background */}
    <div className="absolute inset-0 opacity-30">
      <svg className="w-full h-full" viewBox="0 0 400 300">
        {/* Grid lines */}
        {[...Array(8)].map((_, i) => (
          <line key={`h-${i}`} x1="0" y1={i * 40} x2="400" y2={i * 40} stroke="#cbd5e1" strokeWidth="1" />
        ))}
        {[...Array(10)].map((_, i) => (
          <line key={`v-${i}`} x1={i * 40} y1="0" x2={i * 40} y2="300" stroke="#cbd5e1" strokeWidth="1" />
        ))}
        {/* Roads */}
        <path d="M0,150 Q100,120 200,150 T400,150" stroke="#94a3b8" strokeWidth="8" fill="none" />
        <path d="M150,0 Q180,100 150,200 T150,300" stroke="#94a3b8" strokeWidth="6" fill="none" />
      </svg>
    </div>

    {/* Vehicle Markers — diverse fleet types */}
    {/* 32-Wheel Heavy Vehicle */}
    <div className="absolute top-[62%] left-[35%] animate-bounce z-20" style={{animationDuration:'3.2s', animationDelay:'0.5s'}}>
      <div className="relative">
        <div className="w-14 h-9 bg-red-700 rounded-lg flex items-center justify-center shadow-md">
          <HeavyVehicle />
        </div>
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-red-700 rotate-45" />
        <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-red-700 whitespace-nowrap bg-white/80 px-1 rounded">32-Wheeler</span>
      </div>
    </div>

    {/* Cab / Car */}
    <div className="absolute top-[22%] left-[20%] animate-bounce z-20" style={{animationDuration:'2.2s'}}>
      <div className="relative">
        <div className="w-11 h-11 bg-accent-orange rounded-full flex items-center justify-center shadow-md">
          <Car className="w-5 h-5 text-white" />
        </div>
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-accent-orange rotate-45" />
        <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-accent-orange whitespace-nowrap bg-white/80 px-1 rounded">Cab</span>
      </div>
    </div>

    {/* Truck */}
    <div className="absolute top-[55%] right-[28%] animate-bounce z-20" style={{animationDuration:'2.8s', animationDelay:'0.3s'}}>
      <div className="relative">
        <div className="w-11 h-11 bg-primary-600 rounded-full flex items-center justify-center shadow-md">
          <Truck className="w-5 h-5 text-white" />
        </div>
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-primary-600 rotate-45" />
        <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-primary-600 whitespace-nowrap bg-white/80 px-1 rounded">Truck</span>
      </div>
    </div>

    {/* Bike */}
    <div className="absolute top-[15%] right-[18%] animate-bounce z-20" style={{animationDuration:'1.8s', animationDelay:'0.6s'}}>
      <div className="relative">
        <div className="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center shadow-md">
          <Bike className="w-4 h-4 text-white" />
        </div>
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-pink-500 rotate-45" />
        <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-pink-500 whitespace-nowrap bg-white/80 px-1 rounded">Bike</span>
      </div>
    </div>

    {/* Bus */}
    <div className="absolute bottom-[38%] left-[48%] animate-bounce z-20" style={{animationDuration:'3s', animationDelay:'0.9s'}}>
      <div className="relative">
        <div className="w-11 h-11 bg-green-500 rounded-full flex items-center justify-center shadow-md">
          <Bus className="w-5 h-5 text-white" />
        </div>
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-green-500 rotate-45" />
        <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-green-600 whitespace-nowrap bg-white/80 px-1 rounded">Bus</span>
      </div>
    </div>

    {/* Container Truck */}
    <div className="absolute top-[60%] left-[15%] animate-bounce z-20" style={{animationDuration:'2.5s', animationDelay:'0.4s'}}>
      <div className="relative">
        <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center shadow-md">
          <ContainerTruck />
        </div>
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-slate-700 rotate-45" />
        <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-slate-700 whitespace-nowrap bg-white/80 px-1 rounded">Container</span>
      </div>
    </div>

    {/* Van */}
    <div className="absolute top-[20%] left-[55%] animate-bounce z-20" style={{animationDuration:'2s', animationDelay:'1.1s'}}>
      <div className="relative">
        <div className="w-9 h-9 bg-amber-500 rounded-full flex items-center justify-center shadow-md">
          <Truck className="w-4 h-4 text-white" />
        </div>
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-amber-500 rotate-45" />
        <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-amber-600 whitespace-nowrap bg-white/80 px-1 rounded">Van</span>
      </div>
    </div>

    {/* Auto-Rickshaw */}
    <div className="absolute bottom-[42%] right-[12%] animate-bounce z-20" style={{animationDuration:'2.3s', animationDelay:'0.7s'}}>
      <div className="relative">
        <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center shadow-md">
          <AutoRickshaw />
        </div>
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-yellow-400 rotate-45" />
        <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-yellow-600 whitespace-nowrap bg-white/80 px-1 rounded">Auto</span>
      </div>
    </div>

    {/* Geofence Circle */}
    <div className="absolute top-1/3 left-1/3 z-10">
      <div className="w-32 h-32 border-2 border-dashed border-accent-orange/40 rounded-full animate-pulse" />
    </div>

    {/* Live Indicator */}
    <div className="absolute top-4 right-4 sm:top-6 sm:right-6 bg-white rounded-lg shadow-sm px-3 py-1.5 sm:px-4 sm:py-2 flex items-center gap-2 z-30">
      <span className="w-2 h-2 bg-accent-green rounded-full animate-pulse" />
      <span className="text-xs sm:text-sm font-semibold text-navy-950">Live</span>
    </div>

    {/* Stats Overlay with Animated Counters */}
    <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 bg-white/90 backdrop-blur rounded-xl p-3 sm:p-4 shadow-sm z-30">
      <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
        <div>
          <AnimatedCounter value={24} />
          <div className="text-[10px] sm:text-xs text-gray-500">Active</div>
        </div>
        <div>
          <AnimatedCounter value={142} suffix={"Km Today"} />
        </div>
        <div>
          <AnimatedCounter value={99.9} />
          <div className="text-[10px] sm:text-xs text-gray-500">On Time</div>
        </div>
      </div>
    </div>
  </div>
);

const FeatureTracking: React.FC = () => {
  return (
    <FeatureShowcase
      id="tracking"
      eyebrow="Live Fleet Visibility"
      title="Know exactly where every vehicle is,"
      highlight="in real-time"
      description="GPS tracking with 10-second refresh rates, intelligent geofencing alerts, and route optimization that keeps your fleet on schedule and your customers informed."
      capabilities={[
        "GPS tracking with 10-second refresh",
        "Smart geofencing alerts",
        "Route optimization AI",
        "Share live location with customers"
      ]}
      visual={<TrackingVisual />}
      stats={[
        { value: "10s", label: "Refresh Rate" },
        { value: "99.9%", label: "Uptime" },
        { value: "24/7", label: "Monitoring" }
      ]}
      ctaText="See Live Tracking in Action"
    />
  );
};

export default FeatureTracking;
